// Transport for the client analytics beacon (the engine builds the payload;
// this module gets it to /api/ht).
//
// The subtlety worth knowing: `navigator.sendBeacon` returns **false** rather
// than throwing when the body would exceed the page's shared ~64KiB in-flight
// keepalive quota (https://fetch.spec.whatwg.org/#http-network-or-cache-fetch),
// and the batch is then silently dropped. An ad-heavy listicle flushing 30s of
// impressions can reach that. So a rejected send is halved and re-sent until
// each piece fits — the same strategy posthog-js uses in its own `_sendBeacon`,
// which we don't get for free because we run no SDK.

import type { PageRollup, TrackedEvent } from './events'
import type { NewsletterBeaconContext } from './newsletter-source'

export interface BeaconPayload {
  events: TrackedEvent[]
  allEvents: TrackedEvent[]
  rollup: PageRollup
  requestId: string
  distinctId: string
  locale: string
  // Client clock (Date.now()) at flush time, in the same frame as each event's
  // `timestamp`. Forwarded to PostHog as `sent_at` so it can correct client
  // clock skew. Optional so a beacon from an older in-flight bundle still parses.
  sentAt?: number
  // Present only for newsletter-attributed pageviews. This is transport
  // metadata for the ingest backstop; it is not emitted as a separate event.
  newsletter?: NewsletterBeaconContext
}

/** 'sent' — handed to the browser. 'rejected' — refused for size, worth
 *  splitting. 'unavailable' — the environment has no usable transport, so
 *  splitting would just repeat the same failure. */
type SendResult = 'sent' | 'rejected' | 'unavailable'

const postOnce = (path: string, payload: BeaconPayload): SendResult => {
  let blob: Blob
  try {
    blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
  } catch {
    // Some privacy modes / in-app webviews disable the Blob constructor
    // outright ("Blob usage disabled", HT-APP-71).
    return 'unavailable'
  }

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      return navigator.sendBeacon(path, blob) ? 'sent' : 'rejected'
    }
    // No sendBeacon at all (older browsers, in-app webviews — HT-APP-6F).
    // fetch+keepalive is the equivalent: it survives unload and draws on the
    // same quota. Its over-quota failure is an async rejection we can't read
    // here, so it gets no split — one attempt, best effort.
    if (typeof fetch === 'function') {
      void fetch(path, { method: 'POST', body: blob, keepalive: true }).catch(() => {})
      return 'sent'
    }
  } catch {
    return 'unavailable'
  }
  return 'unavailable'
}

// Last resort for a payload that can't be split any further. A plain fetch is
// NOT subject to the keepalive quota, so an oversized single event still lands
// — but it won't survive an unload, which is the price of getting here.
const postUnbounded = (path: string, payload: BeaconPayload): void => {
  if (typeof fetch !== 'function') {
    return
  }
  try {
    void fetch(path, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' },
    }).catch(() => {})
  } catch {
    // Nothing left to try; drop the batch rather than throw out of an unload flush.
  }
}

// The rollup is an INCREMENT applied server-side (upsert_analytics_rollup), not
// a snapshot — it must ride exactly one chunk or the daily counts double. The
// route no-ops on an empty `counts`, so blanking it is the safe way to carry
// the rest of the payload shape.
const withoutRollupCounts = (payload: BeaconPayload): BeaconPayload => ({
  ...payload,
  rollup: { ...payload.rollup, counts: {} },
})

/** Send a payload, halving it on a size rejection until each piece fits. */
export const sendBeaconPayload = (path: string, payload: BeaconPayload): void => {
  const result = postOnce(path, payload)
  if (result === 'sent' || result === 'unavailable') {
    return
  }

  if (payload.allEvents.length <= 1) {
    postUnbounded(path, payload)
    return
  }

  const mid = Math.ceil(payload.allEvents.length / 2)
  const chunks = [payload.allEvents.slice(0, mid), payload.allEvents.slice(mid)]

  chunks.forEach((chunk, i) => {
    const inChunk = new Set(chunk)
    sendBeaconPayload(path, {
      ...(i === 0 ? payload : withoutRollupCounts(payload)),
      allEvents: chunk,
      // `events` (the PostHog-bound sample) holds the SAME object references as
      // `allEvents` — processEvent pushes one object to both — so partition by
      // identity. Keying on event+timestamp instead would put two events fired
      // in the same millisecond into both halves and double-send them.
      events: payload.events.filter((e) => inChunk.has(e)),
    })
  })
}
